import { useMemo, useRef } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { HourDivider } from './HourDivider'
import { MovementCard } from './MovementCard'
import { useHourGroups } from './useHourGroups'
import type { DetailLevel, MovementCallbacks } from './types'
import type { ScheduleItem } from './useTodayBoardData'

interface VirtualHourListProps {
  items: ScheduleItem[]
  detailLevel: DetailLevel
  callbacks: MovementCallbacks
}

type Row =
  | { kind: 'divider'; key: string; label: string }
  | { kind: 'card'; key: string; item: ScheduleItem }

/** Estimated row heights (px) — used only for the initial layout; real heights are
 *  measured dynamically so variable-height cards (detail levels) scroll correctly. */
const DIVIDER_HEIGHT = 28
const CARD_HEIGHT = 72

/**
 * Virtualized version of the hour-grouped movement list. Flattens hour groups into a flat
 * row list (dividers + cards) and renders only the visible window, so a 1000+-row Masters
 * day stays smooth.
 */
export function VirtualHourList({ items, detailLevel, callbacks }: VirtualHourListProps) {
  const groups = useHourGroups(items)
  const scrollRef = useRef<HTMLDivElement>(null)

  const rows = useMemo<Row[]>(() => {
    const flat: Row[] = []
    let cardIndex = 0
    for (const group of groups) {
      flat.push({ kind: 'divider', key: `h-${group.hour}`, label: group.label })
      for (const item of group.items) {
        // allocationId is empty for schedule-only items (e.g. departures), so it is NOT
        // unique. Build a guaranteed-unique key — the virtualizer caches measured row
        // heights by key, and collisions make cards fall back to the estimate and overlap.
        const id = item.scheduleId || item.allocationId || ''
        flat.push({ kind: 'card', key: `c-${id}-${cardIndex}`, item })
        cardIndex++
      }
    }
    return flat
  }, [groups])

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (i) => (rows[i].kind === 'divider' ? DIVIDER_HEIGHT : CARD_HEIGHT),
    overscan: 8,
    getItemKey: (i) => rows[i].key,
  })

  if (items.length === 0) {
    return <p className="text-xs text-muted-foreground px-3 py-4">None scheduled</p>
  }

  return (
    <div ref={scrollRef} className="h-full overflow-y-auto pr-1">
      <div className="relative w-full" style={{ height: virtualizer.getTotalSize() }}>
        {virtualizer.getVirtualItems().map((vRow) => {
          const row = rows[vRow.index]
          return (
            <div
              key={vRow.key}
              data-index={vRow.index}
              ref={virtualizer.measureElement}
              className="absolute left-0 top-0 w-full"
              style={{ transform: `translateY(${vRow.start}px)` }}
            >
              {row.kind === 'divider' ? (
                <HourDivider label={row.label} />
              ) : (
                <div className="pb-2">
                  <MovementCard
                    item={row.item}
                    detailLevel={detailLevel}
                    onRowClick={callbacks.onRowClick}
                    onCheckIn={callbacks.onCheckIn}
                    onCheckOut={callbacks.onCheckOut}
                    onRegister={callbacks.onRegister}
                    onShowDetails={callbacks.onShowDetails}
                  />
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
